import { useEffect, useState, type ReactNode } from 'react';
import { ChatCircleText, Eye, Microphone, MicrophoneSlash, PhoneDisconnect } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { usePendingApprovalsForSession } from '~/features/chat/pending-approval-card';
import { useApprove, useReject, type ApproveResponse } from '~/hooks/mutations/use-approval-actions';
import { toast } from 'sonner';
import { callBarLabel, muteControl } from '../voice-call-bar';
import { useHandsFreeCall, type HandsFreeCallValue } from './hands-free-call';
import { HandsFreeCallJobs } from './hands-free-call-jobs';

/**
 * FRONT-254: the minimized call — a thin live band across the top of the app,
 * above every page AND above an open chat or document window, so it is always
 * reachable. It shows whenever a hands-free call is up and no composer for
 * the call's own chat is on screen (closing that window minimizes the call).
 *
 * Two things can drop down under it: the quiet-call warning, and a waiting
 * approval for the call's chat with Yes / No — answered in place, without
 * closing whatever the person is reading. A spoken yes still works through
 * the existing voice path; both resolve the same approval.
 */
export function HandsFreeCallStrip() {
  const hf = useHandsFreeCall();
  // Keyed on the call, not the flag: a flags refetch that fails mid-call
  // reads the flag as off, and a live microphone must never lose its controls.
  if (!hf || !hf.active || hf.composerAttached || !hf.binding) return null;
  return <Strip hf={hf} />;
}

/** 32px icon buttons in a 40px-tall band: a pseudo-element extends each hit
 *  area to 40×40 without changing the layout, and a press scales to 0.96. */
const STRIP_ICON_BUTTON = "relative after:absolute after:-inset-1 after:content-[''] active:scale-[0.96]";
const ICON_SWAP = 'absolute inset-0 transition-[opacity,scale,filter] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none';
const ICON_IN = 'opacity-100 scale-100 blur-0';
const ICON_OUT = 'opacity-0 scale-[0.25] blur-[4px]';

/** `mm:ss`, or `h:mm:ss` past an hour. */
export function formatCallTimer(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

function Strip({ hf }: { hf: HandsFreeCallValue }) {
  const binding = hf.binding!;
  const live = hf.state === 'live';
  const now = useNow(live);
  const label = callBarLabel(hf.state, hf.status, hf.muted);
  const mute = muteControl(hf.state, hf.muted);
  const line = hf.assistantLine || hf.lastSaid;
  // Muted outranks what EDDY last said; with nothing said yet, the status.
  let lineContent: ReactNode = <span className="text-muted-foreground">{label}</span>;
  if (hf.muted) lineContent = <span className="text-muted-foreground">Muted</span>;
  else if (line) lineContent = line;
  // Only what the call is waiting on right now — a deferred row from earlier
  // in the chat belongs to the chat window's own card, with its age shown.
  const approvals = usePendingApprovalsForSession(binding.sessionId).filter((a) => a.waitingMode !== 'deferred');
  const ask = approvals[0] ?? null;
  const approve = useApprove();
  const reject = useReject();
  const deciding = approve.isPending || reject.isPending;
  // Never settle optimistically: the row leaves once the list refetches; a
  // failed answer, or a run that failed or was refused, is said out loud.
  const onDecisionError = () => toast.error("That answer didn't go through. Try again, or answer in the chat.");
  const onApproved = (data: ApproveResponse | undefined) => {
    if (data?.executed === 'failed' || data?.executed === 'refused') {
      toast.error(data.message ?? "Approved, but it didn't go through.");
    }
  };

  return (
    <div
      role="region"
      aria-label="Voice call"
      data-testid="hands-free-call-strip"
      // Opaque on purpose (a tint mixed into the page background, not a
      // see-through fill): it sits over the chat window's blurred backdrop,
      // which would otherwise show through it. `relative z-[60]` lifts it
      // above that window (z-50) while staying in the page flow, so the app
      // below moves down instead of being covered.
      className="relative z-[60] shrink-0 border-b border-[var(--eddy-blue-line)] px-3 py-1.5 animate-in fade-in slide-in-from-top-1 duration-200 motion-reduce:animate-none"
      style={{ backgroundColor: 'color-mix(in oklab, var(--eddy-blue) 9%, var(--background))' }}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="relative flex size-2.5 shrink-0" aria-hidden>
          {live && !hf.muted && (
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-[var(--eddy-blue)] opacity-60 motion-reduce:animate-none" />
          )}
          <span className="relative inline-flex size-2.5 rounded-full bg-[var(--eddy-blue)]" />
        </span>
        {/* The whole label is a way back to the call's chat, not only the icon. */}
        <button
          type="button"
          onClick={hf.openStartingChat}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)] cursor-pointer"
          data-testid="hands-free-call-label"
        >
          <span className="shrink-0 text-sm font-semibold text-[var(--eddy-blue-text)]">On a call with EDDY</span>
          <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground" data-testid="hands-free-call-timer">
            {hf.startedAt ? formatCallTimer(now - hf.startedAt) : label}
          </span>
          <span className="min-w-0 flex-1 truncate text-xs text-foreground/80" data-testid="hands-free-call-line">
            {lineContent}
            <span className="ml-2 inline-flex items-center gap-1 align-middle text-muted-foreground" data-testid="hands-free-call-sees">
              <Eye size={12} aria-hidden /> EDDY sees: {hf.screenLabel}
            </span>
          </span>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={STRIP_ICON_BUTTON}
          onClick={hf.openStartingChat}
          aria-label="Open the call's chat"
          tooltip={binding.title ? `Open "${binding.title}"` : 'Open the chat this call started in'}
          data-testid="hands-free-call-open-chat"
        >
          <ChatCircleText size={16} />
        </Button>
        <Button
          type="button"
          variant={hf.muted ? 'secondary' : 'ghost'}
          size="icon-sm"
          className={STRIP_ICON_BUTTON}
          onClick={hf.toggleMute}
          disabled={mute.disabled}
          aria-label={mute.label}
          tooltip={mute.tooltip}
          data-testid="hands-free-call-mute"
        >
          {/* Both glyphs stay mounted and cross-fade, so the swap reads as one control changing state. */}
          <span className="relative inline-flex size-4">
            <Microphone size={16} className={`${ICON_SWAP} ${hf.muted ? ICON_OUT : ICON_IN}`} aria-hidden />
            <MicrophoneSlash size={16} className={`${ICON_SWAP} ${hf.muted ? ICON_IN : ICON_OUT}`} aria-hidden />
          </span>
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={hf.stop}
          className="gap-1.5 active:scale-[0.96]"
          tooltip="End the call — the recap saves to its chat"
          data-testid="hands-free-call-hang-up"
        >
          <PhoneDisconnect size={14} weight="fill" /> Hang up
        </Button>
      </div>

      {/* FRONT-2991: the jobs this call handed to EDDY (one-brain calls only; none otherwise). */}
      <HandsFreeCallJobs sessionId={binding.sessionId} active={hf.active} openStartingChat={hf.openStartingChat} />

      {hf.quietSecondsLeft !== null && (
        <div
          className="mt-1.5 flex flex-wrap items-center gap-2 rounded-lg bg-card px-3 py-2 shadow-[0_0_0_1px_var(--line),var(--shadow-sm)] animate-in fade-in slide-in-from-top-1 duration-150 motion-reduce:animate-none"
          role="status"
          data-testid="hands-free-call-quiet"
        >
          <span className="min-w-0 flex-1 text-sm">
            Nobody has spoken for 5 minutes. Ending the call in{' '}
            <span className="font-semibold tabular-nums">{hf.quietSecondsLeft} s</span>.
          </span>
          <Button type="button" size="sm" className="active:scale-[0.96]" onClick={hf.keepCall} data-testid="hands-free-call-keep">
            Keep call
          </Button>
        </div>
      )}

      {ask && (
        <div
          className="mt-1.5 flex flex-wrap items-center gap-2 rounded-lg bg-card px-3 py-2 shadow-[0_0_0_1px_var(--line),var(--shadow-sm)] animate-in fade-in slide-in-from-top-1 duration-150 motion-reduce:animate-none"
          role="alertdialog"
          aria-label="EDDY is asking for your OK"
          data-testid="hands-free-call-approval"
        >
          <span className="min-w-0 flex-1 text-sm">
            <span className="font-medium">{ask.description}</span>
            <span className="ml-2 text-xs text-muted-foreground">Click, or say yes or no.</span>
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="active:scale-[0.96]"
            disabled={deciding}
            onClick={() => reject.mutate(ask.id, { onError: onDecisionError })}
            data-testid="hands-free-call-reject"
          >
            No
          </Button>
          <Button
            type="button"
            size="sm"
            className="active:scale-[0.96]"
            disabled={deciding}
            onClick={() => approve.mutate(ask.id, { onSuccess: onApproved, onError: onDecisionError })}
            data-testid="hands-free-call-approve"
          >
            Yes
          </Button>
        </div>
      )}
    </div>
  );
}
